import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TopNavigationConfig } from '@frontend/sports/common/client-config-data-access';
import { LDFeatureFlags } from '@frontend/sports/launch-darkly/feature';
import { TopMenuContentItem } from '@frontend/sports/navigation/core/feature/models';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { LaunchDarklyService } from '@frontend/vanilla/features/launch-darkly';
import { Observable, Subject, combineLatest, distinctUntilChanged, first, map, of, startWith, switchMap } from 'rxjs';

import { FixedNavigationItemsProvider } from './providers/fixed-navigation-items.provider';
import { LastSelectedSportProvider } from './providers/last-selected-sport.provider';
import { SportNavigationItemsProvider } from './providers/sport-navigation-items.provider';
import { TopNavigationItemsProvider } from './providers/top-navigation-items.provider';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private navigationConfig = inject(TopNavigationConfig);
    private trackingService = inject(TrackingService);
    private topItemsProvider = inject(TopNavigationItemsProvider);
    private fixedItemsProvider = inject(FixedNavigationItemsProvider);
    private sportsItemsProvider = inject(SportNavigationItemsProvider);
    private lastSportProvider = inject(LastSelectedSportProvider);
    private launchDarklyService = inject(LaunchDarklyService);

    private _topNavigation = signal<TopMenuContentItem[]>([]);
    get topNavigation() {
        return this._topNavigation();
    }

    private separatorMarker = 'before-separator';
    private favouritesMenuName = 'favourites';

    private readonly init$ = new Subject<void>();

    constructor() {
        combineLatest([this.init$, this.navigationConfig.whenReady])
            .pipe(
                first(),
                switchMap(() =>
                    combineLatest([
                        this.topItemsProvider.navigationItems$.pipe(distinctUntilLengthChanged()),
                        this.fixedItemsProvider.navigationItems$.pipe(distinctUntilLengthChanged()),
                        this.sportsItemsProvider.sportItems$.pipe(distinctUntilLengthChanged()),
                        this.lastSportProvider.lastSport$.pipe(distinctUntilChanged((prev, next) => next?.name === prev?.name)),
                        this.launchDarklyService.getFeatureFlagValue$(LDFeatureFlags.FavouritesNavigationMenuPosition).pipe(startWith(undefined)),
                    ]),
                ),
                switchMap(([topNavigationItems, fixedItems, sportsItems, selectedSport, navigationExperiment]) => {
                    if (this.useFallbackNavigation(topNavigationItems, fixedItems, sportsItems)) {
                        return this.getDefaultItems(selectedSport, navigationExperiment);
                    }
                    return of(this.buildNavigation(topNavigationItems, fixedItems, sportsItems, selectedSport, navigationExperiment));
                }),
                takeUntilDestroyed(),
            )
            .subscribe((items) => {
                this._topNavigation.set(items);
            });
    }

    init() {
        this.init$.next();
    }

    tracking(trackingPagename?: string, preFix?: string): void {
        if (!trackingPagename) {
            return;
        }
        const value = this.getTrackingValue(trackingPagename, preFix);

        this.trackingService.track(trackingConstants.EVENT_NAVIGATION_MENUS, {
            [trackingConstants.PAGE_NAVIGATION_MENUS]: value,
        });
    }

    private getDefaultItems(selectedSport?: TopMenuContentItem, navigationExperiment?: number): Observable<TopMenuContentItem[]> {
        return combineLatest([this.topItemsProvider.getDefaultItems(), this.sportsItemsProvider.getSportsItems(7)]).pipe(
            map(([topDefaultItems, sportsDefaultItems]) =>
                this.buildNavigation(
                    topDefaultItems,
                    this.fixedItemsProvider.getDefaultItems(),
                    sportsDefaultItems,
                    selectedSport,
                    navigationExperiment,
                ),
            ),
        );
    }

    private useFallbackNavigation(mainItems: TopMenuContentItem[], fixedItems: TopMenuContentItem[], sportsItems: TopMenuContentItem[]) {
        const emptyMainMenuOrOnlyHomeItem = mainItems?.length <= 1;
        return emptyMainMenuOrOnlyHomeItem && !fixedItems?.length && !sportsItems?.length;
    }

    private buildNavigation(
        mainItems: TopMenuContentItem[],
        fixedItems: TopMenuContentItem[],
        sportsItems: TopMenuContentItem[],
        sport?: TopMenuContentItem,
        navigationExperiment?: number,
    ): TopMenuContentItem[] {
        const isV2Navigation = !!this.navigationConfig.topNavigationItems?.length;
        if (isV2Navigation) {
            const allItems = [...mainItems, ...fixedItems];
            const { sportsIndex, selectedSportIndex } = this.recalculateIndexes(allItems);

            if (sportsIndex >= 0) {
                const beforeSports = allItems.slice(0, sportsIndex);
                const afterSports = allItems.slice(sportsIndex);

                const topNavigationItems = [...beforeSports, ...sportsItems, ...afterSports];

                this.addSelectedSport(topNavigationItems, isV2Navigation, sport, selectedSportIndex);
                this.moveMenuItemToPosition(topNavigationItems, navigationExperiment);

                return topNavigationItems;
            } else {
                this.addSelectedSport(allItems, isV2Navigation, sport, selectedSportIndex);
                this.moveMenuItemToPosition(allItems, navigationExperiment);

                return allItems;
            }
        } else {
            const topNavigationItems = [...mainItems, ...sportsItems, ...fixedItems];

            this.addSelectedSport(topNavigationItems, isV2Navigation, sport);
            this.moveMenuItemToPosition(topNavigationItems, navigationExperiment);

            return topNavigationItems;
        }
    }

    private recalculateIndexes(items: TopMenuContentItem[]): { sportsIndex: number; selectedSportIndex: number } {
        const sportsIndex = this.navigationConfig.sportsConfig.topSports.index;
        const selectedSportIndex = this.navigationConfig.sportsConfig.selectedSport.index;
        const allConfigItems = [...this.navigationConfig.topNavigationItems, ...this.navigationConfig.fixedItems];

        let sportsIndexUpdated = sportsIndex;
        let selectedSportIndexUpdated = selectedSportIndex;

        allConfigItems.forEach((item, index) => {
            if ((item.condition || !item.activeName) && !items.some((i) => i.name === item.activeName)) {
                if (index < sportsIndex) {
                    sportsIndexUpdated--;
                }

                if (index < selectedSportIndex) {
                    selectedSportIndexUpdated--;
                }
            }
        });

        return { sportsIndex: sportsIndexUpdated, selectedSportIndex: selectedSportIndexUpdated };
    }

    private addSelectedSport(items: TopMenuContentItem[], isV2Navigation: boolean, sport?: TopMenuContentItem, index = -1): void {
        if (sport && !items.some((item) => item.name === sport.name)) {
            if (isV2Navigation && index >= 0) {
                items.splice(index, 0, sport);
            } else if (!isV2Navigation) {
                // find the item with separator's index in afterSports array and insert selected sport right before it
                const selectedSportIndex = items.findIndex((i) => i.class.includes(this.separatorMarker));

                items.splice(selectedSportIndex, 0, sport);
            }
        }
    }

    private getTrackingValue(trackingPagename: string, preFix?: string): string {
        return preFix ? `${preFix.toString()}_${trackingPagename}` : trackingPagename;
    }

    private moveMenuItemToPosition(menuItems: TopMenuContentItem[], position?: number): void {
        if (!position) return;

        const index = menuItems.findIndex((item) => item.name === this.favouritesMenuName);
        if (index === -1) return;

        const [element] = menuItems.splice(index, 1);
        menuItems.splice(position - 1, 0, element);
    }
}

export function distinctUntilLengthChanged<T>() {
    return distinctUntilChanged<T[]>((prev, next) => prev.length === next.length);
}
